import { useEffect, useState, type ReactNode } from "react";
import { ClerkLoaded, ClerkLoading } from "@clerk/clerk-react";

/**
 * Renders auth-dependent UI only once Clerk is up, with something in its place
 * meanwhile.
 *
 * Without this, <SignedIn>/<SignedOut> render nothing at all until Clerk's script
 * loads — so a slow connection or a Clerk outage produces a page with no sign-in
 * button and no explanation. A blank space where the door should be reads as a
 * broken product, and the user has no way to tell it apart from one.
 */
export function AuthArea({ placeholder, children }: { placeholder: ReactNode; children: ReactNode }) {
  return (
    <>
      <ClerkLoading>{placeholder}</ClerkLoading>
      <ClerkLoaded>{children}</ClerkLoaded>
    </>
  );
}

/** After a few seconds of no Clerk, say so rather than spinning forever. */
export function AuthStalled({ afterMs = 6000 }: { afterMs?: number }) {
  const [stalled, setStalled] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setStalled(true), afterMs);
    return () => clearTimeout(t);
  }, [afterMs]);

  if (!stalled) return null;
  return (
    <p className="notice notice-err" role="alert">
      Sign-in is taking longer than usual. That's our authentication provider, not your
      connection — the page will work as soon as it responds.
    </p>
  );
}
